import React, { useMemo } from 'react';
import { Form, Input, Select, Typography, Row, Col, Card, Divider, Button, Space } from 'antd';
import { calculateNodeOutputSchema } from '../utils/schemaUtils';

const { Text } = Typography;
const { Option } = Select;

const JoinNodeProperties = ({ config, updateConfig, nodes, edges, selectedNode }) => {
  const form = Form.useFormInstance();
  
  const getColumns = (handleId) => {
    if (!edges || !nodes || !selectedNode) return [];
    
    // Try to find edge matching the specific handle (e.g. 'left' or 'right')
    // Also support legacy handle names if they exist (dataset_left, dataset_right)
    let edge = edges.find(e => 
      e.target === selectedNode.id && 
      (e.targetHandle === handleId || e.targetHandle === `dataset_${handleId}`)
    );

    // Fallback: if no specific handle match, and we are looking for 'left' (first input),
    // take the first edge connected to this node if it's generic
    if (!edge && handleId === 'left') {
        // This is risky if there are multiple edges, but helps if handles are messed up
        const connectedEdges = edges.filter(e => e.target === selectedNode.id);
        if (connectedEdges.length === 1) edge = connectedEdges[0];
    }

    if (!edge) return [];
    
    // Calculate schema dynamically using the utility
    const schema = calculateNodeOutputSchema(edge.source, nodes, edges);

    if (Array.isArray(schema)) {
      return schema
        .filter(col => {
          // Filter out disabled columns if the property exists
          if (typeof col === 'object' && col !== null && 'enabled' in col) {
            return col.enabled !== false;
          }
          return true;
        })
        .map(col => {
          if (typeof col === 'string') return col;
          // Handle different naming conventions
          return col.columnName || col.name || col.field;
        })
        .filter(Boolean); // Remove empty values
    }
    return [];
  };

  // Memoize columns to prevent unnecessary re-renders
  const leftCols = useMemo(() => getColumns('left'), [nodes, edges, selectedNode]);
  const rightCols = useMemo(() => getColumns('right'), [nodes, edges, selectedNode]);

  const handleSelectAll = (field, values) => {
    form.setFieldsValue({ [field]: values });
    updateConfig({ config: { ...config, [field]: values } });
  };

  const handleClearAll = (field) => {
    form.setFieldsValue({ [field]: [] });
    updateConfig({ config: { ...config, [field]: [] } });
  };

  return (
    <>
      <Form.Item label="Join Type" name="joinType" initialValue={config.joinType || 'inner'} rules={[{ required: true }]}>
        <Select>
          <Option value="inner">Inner Join</Option>
          <Option value="left">Left Join</Option>
          <Option value="right">Right Join</Option>
          <Option value="outer">Outer Join (Full)</Option>
        </Select>
      </Form.Item>

      <Divider orientation="left" style={{ fontSize: '12px' }}>Left Dataset Configuration</Divider>
      
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item label="Left Join Column" name="leftColumn" initialValue={config.leftColumn || ''} rules={[{ required: true }]}>
            <Select placeholder="Select column" showSearch allowClear>
               {leftCols.map(col => <Option key={col} value={col}>{col}</Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Right Join Column" name="rightColumn" initialValue={config.rightColumn || ''} rules={[{ required: true }]}>
            <Select placeholder="Select column" showSearch allowClear>
               {rightCols.map(col => <Option key={col} value={col}>{col}</Option>)}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" style={{ fontSize: '12px' }}>Column Selection</Divider>

      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: '12px' }}>Left Columns to Include</Text>
        <Space size="small">
            <Button size="small" type="link" onClick={() => handleSelectAll('leftColumns', leftCols)} style={{ padding: 0, fontSize: '11px' }}>Select All</Button>
            <Button size="small" type="link" onClick={() => handleClearAll('leftColumns')} style={{ padding: 0, fontSize: '11px' }} danger>Clear</Button>
        </Space>
      </div>
      <Form.Item name="leftColumns" initialValue={config.leftColumns || []}>
        <Select mode="tags" placeholder="Select columns from left dataset (empty = all)" allowClear>
          {leftCols.map(col => <Option key={col} value={col}>{col}</Option>)}
        </Select>
      </Form.Item>

      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: '12px' }}>Right Columns to Include</Text>
        <Space size="small">
            <Button size="small" type="link" onClick={() => handleSelectAll('rightColumns', rightCols)} style={{ padding: 0, fontSize: '11px' }}>Select All</Button>
            <Button size="small" type="link" onClick={() => handleClearAll('rightColumns')} style={{ padding: 0, fontSize: '11px' }} danger>Clear</Button>
        </Space>
      </div>
      <Form.Item name="rightColumns" initialValue={config.rightColumns || []}>
        <Select mode="tags" placeholder="Select columns from right dataset (empty = all)" allowClear>
          {rightCols.map(col => <Option key={col} value={col}>{col}</Option>)}
        </Select>
      </Form.Item>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item label="Left Prefix" name="leftPrefix" initialValue={config.leftPrefix || ''}>
            <Input placeholder="Prefix for left columns (optional)" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Right Prefix" name="rightPrefix" initialValue={config.rightPrefix || ''}>
            <Input placeholder="Prefix for right columns (optional)" />
          </Form.Item>
        </Col>
      </Row>

      <div style={{ padding: '8px 12px', backgroundColor: '#f9f9f9', borderRadius: '4px', fontSize: '12px', marginTop: '12px' }}>
        <Text type="secondary">
          <strong>Join Node:</strong><br/>
          • <strong>Inner:</strong> Only matching records from both datasets<br/>
          • <strong>Left:</strong> All records from left + matching from right<br/>
          • <strong>Right:</strong> All records from right + matching from left<br/>
          • <strong>Outer:</strong> All records from both datasets<br/>
          • Requires two input connections (datasets to join)
        </Text>
      </div>
    </>
  );
};

export default JoinNodeProperties;